import { Link } from "react-router";
import { ChevronRight, X } from "lucide-react";
import type { MonthlySummary } from "@/lib/expenses";
import { CATEGORY_EMOJI, CATEGORY_COLOR } from "@/lib/categories";
import type { Category } from "@/lib/categories";
import { formatMoney } from "@/lib/format";

// Interactive spending breakdown: a donut for the at-a-glance split plus a
// ranked, tappable list. Tapping a category filters the dashboard's expense
// list (via ?category=), so the breakdown doubles as navigation.
export function CategoryBreakdown({
  summary,
  month,
  active,
  scope,
}: {
  summary: MonthlySummary;
  month: string;
  active?: string;
  scope?: string;
}) {
  const cats = summary.byCategory;
  if (cats.length === 0) return null;

  const total = summary.totalCents || 1;
  const activeCat = active
    ? cats.find((c) => c.category === active)
    : undefined;

  // Donut segments (r chosen so circumference = 100 → dash values are %).
  // Offset = sum of preceding slices; n is tiny so the O(n²) prefix is fine.
  const segments = cats.map((c, i) => ({
    c,
    pct: (c.totalCents / total) * 100,
    offset: cats
      .slice(0, i)
      .reduce((s, x) => s + (x.totalCents / total) * 100, 0),
  }));

  const link = (cat?: string) => {
    const params = new URLSearchParams({ month });
    if (cat) params.set("category", cat);
    if (scope) params.set("scope", scope);
    return `/?${params.toString()}`;
  };

  // What the donut centre shows: the filtered category, else the grand total.
  const centerLabel = activeCat ? activeCat.category : "Total";
  const centerValue = formatMoney(
    activeCat ? activeCat.totalCents : summary.totalCents,
    summary.currency,
  );

  return (
    <div className="rounded-[1.75rem] bg-card p-5 shadow-sm ring-1 ring-black/[0.05]">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Spending breakdown</h2>
        {active && (
          <Link
            to={link()}
            className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground active:bg-border"
          >
            <X className="size-3" />
            Clear
          </Link>
        )}
      </div>

      {/* Donut + legend total */}
      <div className="flex items-center gap-5">
        <div className="relative size-32 shrink-0">
          <svg viewBox="0 0 36 36" className="size-full -rotate-90">
            <circle
              cx="18"
              cy="18"
              r="15.915"
              fill="none"
              stroke="var(--muted)"
              strokeWidth="3.5"
            />
            {segments.map((s) => {
              const color = CATEGORY_COLOR[s.c.category as Category];
              const dim = active && active !== s.c.category;
              return (
                <circle
                  key={s.c.category}
                  cx="18"
                  cy="18"
                  r="15.915"
                  fill="none"
                  stroke={color?.hex ?? "#71717a"}
                  strokeWidth={active === s.c.category ? "5" : "3.5"}
                  strokeDasharray={`${s.pct} ${100 - s.pct}`}
                  strokeDashoffset={-s.offset}
                  opacity={dim ? 0.25 : 1}
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="max-w-[5rem] truncate text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground">
              {centerLabel}
            </span>
            <span className="text-base font-bold tabular-nums leading-tight">
              {centerValue}
            </span>
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          {cats.slice(0, 3).map((c) => {
            const color = CATEGORY_COLOR[c.category as Category];
            const pct = Math.round((c.totalCents / total) * 100);
            return (
              <div
                key={c.category}
                className="flex items-center gap-2 text-sm"
              >
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ background: color?.hex }}
                />
                <span className="min-w-0 flex-1 truncate text-muted-foreground">
                  {c.category}
                </span>
                <span className="shrink-0 font-semibold tabular-nums">
                  {pct}%
                </span>
              </div>
            );
          })}
          {cats.length > 3 && (
            <p className="pl-[1.125rem] text-xs text-muted-foreground">
              +{cats.length - 3} more below
            </p>
          )}
        </div>
      </div>

      {/* Ranked, tappable list */}
      <div className="mt-4 space-y-1.5 border-t border-border/70 pt-4">
        {cats.map((c) => {
          const color = CATEGORY_COLOR[c.category as Category];
          const pct = Math.round((c.totalCents / total) * 100);
          const isActive = active === c.category;
          return (
            <Link
              key={c.category}
              to={isActive ? link() : link(c.category)}
              className={`flex items-center gap-3 rounded-2xl px-2 py-2 transition-colors active:bg-muted ${
                isActive ? "bg-muted" : ""
              }`}
            >
              <span
                className={`flex size-9 shrink-0 items-center justify-center rounded-full text-base ${color?.tint ?? "bg-zinc-100"}`}
              >
                {CATEGORY_EMOJI[c.category]}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">
                    {c.category}
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {formatMoney(c.totalCents, summary.currency)}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${Math.max(pct, 2)}%`,
                        background: color?.hex,
                      }}
                    />
                  </div>
                  <span className="w-12 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    {pct}% · {c.count}
                  </span>
                </div>
              </div>
              <ChevronRight
                className={`size-4 shrink-0 text-muted-foreground/50 transition-opacity ${
                  isActive ? "opacity-0" : ""
                }`}
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
